import { gql } from '@apollo/client';
import {
  OfflineBackgroundSyncRegistryAdapter,
  OfflineVariantPackInfoAdapter,
} from '@/adapters/offline-mode';
import { OfflineSyncType } from '@/utils/offline-mode';
import { print as printQuery } from 'graphql';
import { useLazyQuery } from '@/hooks/use-lazy-query';
import { VARIANT_PACK_INFO_FRAGMENT } from '@/contexts/variant-pack-info-context-queries';
import {
  VariantPackInfoContext_OfflineInfoListQuery,
  VariantPackInfoContext_OfflineInfoListQueryVariables,
} from '@/types';

const GET_OFFLINE_VARIANT_PACK_INFO_LIST_QUERY = gql`
  query VariantPackInfoContext_OfflineInfoList(
    $orderGuideOnly: Boolean!
    $deliveryDate: String
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
  ) {
    getVariantPackInfoList(
      delivery_date: $deliveryDate
      supplier_id: $supplierUUID
      restaurant_id: $restaurantUUID
      order_guide_only: $orderGuideOnly
    ) {
      ...VariantPackInfoContext_InfoFields
    }
  }

  ${VARIANT_PACK_INFO_FRAGMENT}
`;

/**
 * Custom hook to download and save the variant pack info for a specific restaurant and supplier.
 *
 * This hook uses a lazy query to fetch the variant pack info from the server and then saves it
 * locally using the OfflineVariantPackInfoAdapter. It can fetch either order guide data only
 * or full catalog data based on the catalogEnabled flag.
 *
 * @returns {Object} - An object containing the downloadVariantPackInfo function.
 *
 * @function
 * @name useDownloadVariantPackInfo
 *
 * @example
 * const { downloadVariantPackInfo } = useDownloadVariantPackInfo();
 *
 * // Download and save the variant pack info for a specific restaurant and supplier
 * downloadVariantPackInfo('restaurant-uuid', 'supplier-uuid', true); // Order guide only
 */
export const useDownloadVariantPackInfo = () => {
  const getOfflineVariantPackInfo = useLazyQuery<
    VariantPackInfoContext_OfflineInfoListQuery,
    VariantPackInfoContext_OfflineInfoListQueryVariables
  >(GET_OFFLINE_VARIANT_PACK_INFO_LIST_QUERY, {
    fetchPolicy: 'no-cache',
  });

  /**
   * Fetches and saves the variant pack info for a given restaurant and supplier.
   *
   * @async
   * @param {string} restaurantUUID - The UUID of the restaurant.
   * @param {string} supplierUUID - The UUID of the supplier.
   * @param {boolean} orderGuideOnly - Whether to fetch full catalog data or order guide only.
   */
  const downloadVariantPackInfo = async (
    restaurantUUID: string,
    supplierUUID: string,
    orderGuideOnly = true,
  ) => {
    // Hardcoding the delivery date to undefined, here's a quick explanation:
    //  - Catalog resolvers may return different variant pack info based on the selected delivery date
    //  - Some catalog resolvers require the delivery date, or they will short circuit
    //  - This will need to be resolved for offline mode to work in the long run
    const variables = {
      deliveryDate: undefined,
      orderGuideOnly,
      restaurantUUID,
      supplierUUID,
    };

    const { data: variantPackInfoData } = await getOfflineVariantPackInfo({
      context: {
        offlineSync: true,
      },
      variables,
    });
    const offlineVariantPackInfoAdapter =
      OfflineVariantPackInfoAdapter.getInstance(restaurantUUID, supplierUUID);
    await offlineVariantPackInfoAdapter.save(variantPackInfoData);

    const backgroundSyncRegistryAdapter =
      new OfflineBackgroundSyncRegistryAdapter();
    await backgroundSyncRegistryAdapter.updateSyncMetadata(
      restaurantUUID,
      supplierUUID,
      {
        query: printQuery(GET_OFFLINE_VARIANT_PACK_INFO_LIST_QUERY),
        syncType: OfflineSyncType.VariantPackInfo,
        variables,
      },
    );
  };

  return { downloadVariantPackInfo };
};
